import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Spinner, theme, css, flexCenter } from '@kantera/ui';
import type { Role } from '@kantera/types';
import { PLATFORM_ROLES, withoutParentForChildLogin } from '../utils/userRoles';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * One or more roles that are permitted to access this route.
   * - If omitted, any authenticated user may access it.
   * - If provided, the user must have at least one of the listed roles.
   *   Users without a matching role are redirected to the dashboard (/).
   * Note: 'owner' always inherits the same access as 'admin'.
   */
  requiredRole?: Role | readonly Role[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { user, tenant, loading, passwordRecovery } = useAuth();

  if (loading) {
    return (
      <div style={css(flexCenter(), { minHeight: '100vh', background: theme.colors.bg.primary })}>
        <Spinner />
      </div>
    );
  }

  // Block all app access during password recovery — force user to set password first
  if (passwordRecovery) {
    return <Navigate to="/reset-password" replace />;
  }

  // C23 — `!tenant` alone asked whether an OBJECT existed, not whether an org
  // was resolved. buildTenant can no longer return a tenant without one, but a
  // gate that only checks the wrapper would admit any future shape carrying an
  // empty orgId, and every org-scoped call underneath would then run with no
  // org. Ask for the value, not the container.
  if (!user || !tenant || !String(tenant.orgId ?? '').trim()) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole !== undefined) {
    const allowed: readonly Role[] = Array.isArray(requiredRole)
      ? requiredRole
      : [requiredRole as Role];
    // Owners always have the same access as admins — include owner if admin is allowed.
    let effectiveAllowed: readonly Role[] =
      allowed.includes('admin') && !allowed.includes('owner')
        ? [...allowed, 'owner' as Role]
        : allowed;
    // `founder` is a legacy alias for `platform_admin`. A gate naming either must
    // admit both, or half the Kantera staff are locked out of a page meant for
    // them — and the failure looks like a permissions bug rather than a typo.
    if (effectiveAllowed.some((r) => PLATFORM_ROLES.includes(r))) {
      effectiveAllowed = [...new Set([...effectiveAllowed, ...PLATFORM_ROLES])];
    }
    // KAN-143: a child's own login never counts as holding 'parent', however its roles came to say
    // so (utils/userRoles.ts withoutParentForChildLogin). The API turns it away from every parent
    // page anyway (403 CHILD_ACCOUNT); this keeps those pages from opening at all.
    const heldRoles = withoutParentForChildLogin(tenant.roles);
    const hasAccess = effectiveAllowed.some((r) => heldRoles.includes(r));
    if (!hasAccess) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};
